import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useTenant } from "../contexts/TenantContext";
import { t } from "../lib/i18n";

// ``compact``: a small inline mic for use inside a search input (Blinkit home). The default — a floating
// primary circle — is unchanged for every existing caller.
export default function VoiceSearch({ compact = false }) {
    const { tenant, lang } = useTenant();
    const [open, setOpen] = useState(false);
    const [listening, setListening] = useState(false);
    const [transcript, setTranscript] = useState("");
    const [parsing, setParsing] = useState(false);
    const [error, setError] = useState(null);
    const recogRef = useRef(null);
    const navigate = useNavigate();

    const langMap = { en: "en-IN", hi: "hi-IN", te: "te-IN" };

    const start = () => {
        setError(null);
        setTranscript("");
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            setError("Voice not supported on this browser. Type your search.");
            return;
        }
        const r = new SR();
        r.lang = langMap[lang] || "en-IN";
        r.interimResults = true;
        r.continuous = false;
        r.onresult = (e) => {
            const txt = Array.from(e.results)
                .map((res) => res[0].transcript)
                .join(" ");
            setTranscript(txt);
        };
        r.onerror = (e) => {
            setError(e.error || "Voice error");
            setListening(false);
        };
        r.onend = () => setListening(false);
        recogRef.current = r;
        r.start();
        setListening(true);
    };

    const stop = () => {
        recogRef.current?.stop();
        setListening(false);
    };

    const submit = async () => {
        if (!transcript.trim()) return;
        setParsing(true);
        try {
            const { data } = await api.post("/voice/parse", { transcript, language: lang });
            const q = data.query || transcript;
            setOpen(false);
            navigate(`/search?q=${encodeURIComponent(q)}${data.food_type ? `&food_type=${data.food_type}` : ""}`);
        } catch (e) {
            setError("Could not parse. Try again.");
        } finally {
            setParsing(false);
        }
    };

    useEffect(() => () => recogRef.current?.stop(), []);

    // Super-admin gates this per tenant. Render nothing when disabled so the
    // mic icon never appears on tenants that haven't subscribed.
    if (!tenant?.features?.voice_search) return null;

    return (
        <>
            <button
                data-testid="voice-search-mic"
                onClick={() => setOpen(true)}
                className={compact
                    ? "w-9 h-9 rounded-full flex items-center justify-center text-foreground/70 hover:bg-muted active:scale-95 transition"
                    : "bg-primary text-primary-foreground rounded-full w-11 h-11 flex items-center justify-center shadow-lg active:scale-95 transition"}
                aria-label="Voice search"
            >
                <Mic size={compact ? 18 : 20} />
            </button>

            {open && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4 fade-up">
                    <div className="bg-card rounded-3xl p-6 w-full max-w-md md:max-w-lg text-center relative">
                        <button data-testid="voice-close" onClick={() => { stop(); setOpen(false); }} className="absolute top-4 right-4 text-muted-foreground">
                            <X size={22} />
                        </button>
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Voice Search</p>
                        <button
                            data-testid="voice-mic-btn"
                            onClick={listening ? stop : start}
                            className={`w-24 h-24 rounded-full mx-auto flex items-center justify-center bg-primary text-primary-foreground ${listening ? "mic-pulse" : ""}`}
                        >
                            {listening ? <MicOff size={36} /> : <Mic size={36} />}
                        </button>
                        <p className="mt-6 text-sm text-muted-foreground">{t("voice_hint", lang)}</p>
                        {transcript && (
                            <div className="mt-4 p-3 bg-muted rounded-xl text-sm font-medium text-left" data-testid="voice-transcript">
                                {transcript}
                            </div>
                        )}
                        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
                        <button
                            data-testid="voice-search-go"
                            disabled={!transcript || parsing}
                            onClick={submit}
                            className="mt-5 w-full bg-foreground text-background rounded-full py-3 font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-2"
                        >
                            {parsing && <Loader2 size={18} className="animate-spin" />}
                            Search
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
